import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, Calendar, ChevronRight, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClockOnModal({ open, onOpenChange, onClockIn, isDutyOfficer = false }) {
    const [todaysEvents, setTodaysEvents] = useState([]);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            fetchData();
        }
    }, [open]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [eventsRes, activitiesRes] = await Promise.all([
                axios.get(`${API_URL}/calendar/events/today`),
                axios.get(`${API_URL}/clock-on-activities`)
            ]);
            setTodaysEvents(eventsRes.data);
            setActivities(activitiesRes.data);
        } catch (error) {
            console.error('Failed to fetch clock-on data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectEvent = async (event) => {
        setSubmitting(true);
        try {
            const response = await axios.post(`${API_URL}/attendance/clock-in`, {
                event_id: event.id,
                is_duty_officer: isDutyOfficer
            });
            toast.success(`Clocked in to: ${event.title}`);
            onClockIn(response.data);
            onOpenChange(false);
        } catch (error) {
            toast.error('Failed to clock in');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSelectActivity = async (activity) => {
        setSubmitting(true);
        try {
            const response = await axios.post(`${API_URL}/attendance/clock-in`, {
                activity_id: activity.id,
                activity_name: activity.name,
                is_duty_officer: isDutyOfficer
            });
            toast.success(`Clocked in: ${activity.name}`);
            onClockIn(response.data);
            onOpenChange(false);
        } catch (error) {
            toast.error('Failed to clock in');
        } finally {
            setSubmitting(false);
        }
    };

    // Group activities by category
    const groupedActivities = activities.reduce((acc, activity) => {
        const category = activity.category || 'Other';
        if (!acc[category]) acc[category] = [];
        acc[category].push(activity);
        return acc;
    }, {});

    const formatTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="clock-on-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary" />
                        {isDutyOfficer ? 'Duty Officer Clock-on' : 'Clock-on'}
                    </DialogTitle>
                    <DialogDescription>
                        Select what you're clocking in for
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Today's Calendar Events */}
                        {todaysEvents.length > 0 && (
                            <div>
                                <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    TODAY'S EVENTS
                                </h3>
                                <div className="space-y-2">
                                    {todaysEvents.map((event) => (
                                        <button
                                            key={event.id}
                                            onClick={() => handleSelectEvent(event)}
                                            disabled={submitting}
                                            className="w-full p-3 text-left rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors flex items-center justify-between group"
                                            data-testid={`event-${event.id}`}
                                        >
                                            <div>
                                                <p className="font-medium text-primary">{event.title}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {formatTime(event.start_time)}
                                                    {event.end_time && ` - ${formatTime(event.end_time)}`}
                                                </p>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Activity Categories */}
                        {Object.entries(groupedActivities).map(([category, categoryActivities]) => (
                            <div key={category}>
                                <h3 className="text-sm font-semibold text-muted-foreground mb-2 uppercase">
                                    {category}
                                </h3>
                                <div className="space-y-1">
                                    {categoryActivities.map((activity) => (
                                        <button
                                            key={activity.id}
                                            onClick={() => handleSelectActivity(activity)}
                                            disabled={submitting}
                                            className="w-full p-3 text-left rounded-lg border border-border hover:bg-muted/50 transition-colors flex items-center justify-between group"
                                            data-testid={`activity-${activity.id}`}
                                        >
                                            <span className="font-medium">{activity.name}</span>
                                            <ChevronRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {activities.length === 0 && todaysEvents.length === 0 && (
                            <div className="text-center py-6 text-muted-foreground">
                                <p>No activities available</p>
                            </div>
                        )}
                    </div>
                )}

                <div className="pt-4 border-t">
                    <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => onOpenChange(false)}
                        data-testid="clock-on-modal-cancel"
                    >
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
